import { createBasePDF, BRAND_COLORS } from '@/utils/pdfConfig';
import { drawHeader } from '@/utils/pdfHeader';
import { drawFooter } from '@/utils/pdfFooter';
import { translateTxType } from '@/utils/dictionary';
import apiClient from '@/api/client';
import { SecureStorage } from '@/utils/secureStorage';

export function useInformativeSummary() {

  const formatMoney = (amount) => {
    return Number(amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const user = SecureStorage.getItem('llevo_user');

  const loadImage = (url) => {
    return new Promise((resolve) => {
      if (!url) return resolve(null);
      const proxiedUrl = url.replace('https://frontline.therealdevs.pro', '/api-proxy');
      const img = new Image();
      img.setAttribute('crossOrigin', 'anonymous');
      img.onload = () => resolve(img);
      img.onerror = () => resolve(null);
      img.src = proxiedUrl;
    });
  };



  // NUEVO: Agregamos displayOptions con valores por defecto (todo encendido)
  const generateSummary = async (dossier, displayOptions = {}, globalCatalog = [], titleRpt) => {

    // Configuración de visibilidad de secciones
    const opts = {
      showPerfil: true,
      showProducto: true,
      showResolucion: true,
      showEvaluacion: true,
      showGarantias: true,
      showPlanTeorico: true,
      showPlanActivo: true,
      showTransacciones: true,
      showAnexos: true,
      ...displayOptions
    };
    const getGlobalText = (value, type) => {
        if (!globalCatalog || globalCatalog.length === 0) return String(value || 'N/D');
        const item = globalCatalog.find(g => Number(g.type) === type && String(g.value) === String(value));
        return item ? item.text.toUpperCase() : String(value || 'N/D');
    };
    const nombreUsuario = user?.full_name || 'SISTEMA';
    const {
      loan,
      customer,
      product,
      transactions = [],
      theoretical_plans = [],
      payment_plans = [],
      collaterals = [],
      evaluation = null
    } = dossier;

    try {
      const configRes = await apiClient.get('/company_config');
      const config = Array.isArray(configRes.data) ? configRes.data[0] : configRes.data;
      const logoImg = await loadImage(config.logo_url);

      const doc = createBasePDF('p', 'letter');
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const margin = 15;
      const contentWidth = pageWidth - (margin * 2);

      const headerOptions = {
        companyName: config.trade_name,
        title: titleRpt,
        subtitle: `Impreso el: ${new Date().toLocaleDateString('es-ES')} a las ${new Date().toLocaleTimeString('es-ES')} | Usuario: ${nombreUsuario}`,
        logo: logoImg,
        useColors: false
      };

      // 1. INICIALIZAR PÁGINA
      drawHeader(doc, headerOptions);
      let y = 45;

      // Contador dinámico para los títulos
      let sectionCounter = 1;

      // --- MOTOR DE PAGINACIÓN ---
      const checkPageBreak = (neededSpace) => {
        if (y + neededSpace > pageHeight - 20) {
          doc.addPage();
          drawHeader(doc, headerOptions);
          y = 45;
          return true;
        }
        return false;
      };

      // --- RENDERIZADORES ---
      const drawSectionTitle = (title) => {
        checkPageBreak(15);
        doc.setFillColor(245, 245, 245);
        doc.rect(margin, y, contentWidth, 7, 'F');
        doc.setDrawColor(0, 0, 0);
        doc.setLineWidth(0.3);
        doc.line(margin, y, pageWidth - margin, y);
        doc.line(margin, y + 7, pageWidth - margin, y + 7);

        doc.setFont('helvetica', 'bold');
        doc.setFontSize(9);
        doc.setTextColor(0, 0, 0);
        // Concatenamos el número dinámico con el título
        doc.text(`${sectionCounter++}. ${title.toUpperCase()}`, margin + 2, y + 5);
        y += 12;
      };

      const drawLabelValue = (label, value, x, currentY) => {
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(50, 50, 50);
        doc.text(label, x, currentY);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(0, 0, 0);
        doc.text(value ? String(value) : 'N/A', x + 30, currentY);
      };

      const drawLabelValueLong = (label, value, x, currentY, textMaxWidth) => {
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(50, 50, 50);
        doc.text(label, x, currentY);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(0, 0, 0);
        const textLines = doc.splitTextToSize(value ? String(value) : 'N/A', textMaxWidth);
        doc.text(textLines, x + 30, currentY);
        return textLines.length * 4.5;
      };

      // ====================================================================
      // SECCIÓN: DATOS GENERALES Y DEMOGRÁFICOS DEL CLIENTE
      // ====================================================================
      if (opts.showPerfil) {
        drawSectionTitle('Perfil del Cliente');
        doc.setFontSize(8);
        const col1X = margin;
        const col2X = margin + (contentWidth / 2) + 5;
        let lineH = 5.5;

        drawLabelValue('Nombre:', customer.full_name, col1X, y);
        drawLabelValue('Conocido como:', customer.known_as, col2X, y);
        y += lineH;
        drawLabelValue('Doc. Identidad:', customer.identity_document, col1X, y);
        drawLabelValue('Vencimiento Doc.:', customer.id_expiration_date, col2X, y);
        y += lineH;
        drawLabelValue('Fecha Nac.:', customer.birth_date, col1X, y);
        drawLabelValue('Lugar Nac.:', `${customer.birth_place}, ${customer.birth_department}`, col2X, y);
        y += lineH;
        drawLabelValue('Género:', customer.gender === 'M' ? 'MASCULINO' : 'FEMENINO', col1X, y);

        // DICCIONARIO: Estado Civil
        drawLabelValue('Estado Civil:', translateTxType(customer.marital_status.toUpperCase())?.toUpperCase(), col2X, y);
        y += lineH;
        drawLabelValue('Educación:', customer.education_level?.toUpperCase(), col1X, y);
        drawLabelValue('Dependientes:', `${customer.dependents_count} (${customer.children_count} Hijos)`, col2X, y);
        y += lineH;
        drawLabelValue('Teléfono/Móvil:', `${customer.home_phone} / ${customer.mobile_phone}`, col1X, y);
        drawLabelValue('Email:', customer.email?.toUpperCase(), col2X, y);
        y += lineH;

        let offset = drawLabelValueLong('Dirección:', customer.address, col1X, y, (contentWidth/2) - 35);

        // DICCIONARIO: Zona
        drawLabelValue('Zona:', translateTxType(customer.zone_type.toUpperCase())?.toUpperCase(), col2X, y);
        //drawLabelValue('Coordenadas:', customer.gps_coordinates, col2X, y + 4.5);
        y += offset;

        // DICCIONARIO: Vivienda
        const tipoVivienda = translateTxType(customer.housing_type.toUpperCase())?.toUpperCase();
        drawLabelValue('Vivienda:', `${tipoVivienda} (${customer.years_living_there})`, col1X, y);
        drawLabelValue('Propietario:', customer.owner_name?.toUpperCase(), col2X, y);
        y += lineH;
        drawLabelValue('Profesión:', customer.profession?.toUpperCase(), col1X, y);
        drawLabelValue('Act. Económica:', customer.economic_activity?.toUpperCase(), col2X, y);
        y += lineH + 2;
      }


      // ====================================================================
      // SECCIÓN: PARAMETRIZACIÓN DEL PRODUCTO
      // ====================================================================
      if (opts.showProducto && product) {
        drawSectionTitle('Parámetros del Producto Seleccionado');
        doc.setFontSize(8);
        const col1X = margin;
        const col2X = margin + (contentWidth / 2) + 5;
        let lineH = 5.5;

        drawLabelValue('Nombre:', product.name?.toUpperCase(), col1X, y);
        drawLabelValue('Código Prod.:', product.code, col2X, y);
        y += lineH;
        drawLabelValue('Rango Montos:', `$ ${formatMoney(product.min_amount)} - $ ${formatMoney(product.max_amount)}`, col1X, y);
        drawLabelValue('Rango Plazos:', `${product.min_term_months} - ${product.max_term_months} Meses`, col2X, y);
        y += lineH;
        drawLabelValue('Interés Permitido:', `${product.min_interest_rate}% - ${product.max_interest_rate}%`, col1X, y);
        drawLabelValue('Cuenta Contable:', product.accounting_account, col2X, y);
        y += lineH + 2;
      }


      // ====================================================================
      // SECCIÓN: DATOS DEL CRÉDITO Y RESOLUCIÓN
      // ====================================================================
      if (opts.showResolucion) {
        drawSectionTitle('Resolución del Crédito');
        doc.setFontSize(8);
        const col1X = margin;
        const col2X = margin + (contentWidth / 2) + 5;
        let lineH = 5.5;

        drawLabelValue('Cód. Crédito:', loan.code, col1X, y);

        // DICCIONARIO: Estado del crédito
        drawLabelValue('Estado:', translateTxType(loan.status)?.toUpperCase(), col2X, y);
        y += lineH;
        drawLabelValue('Monto Aprobado:', `$ ${formatMoney(loan.amount)}`, col1X, y);
        drawLabelValue('Monto Total(c/int):', `$ ${formatMoney(loan.total_amount)}`, col2X, y);
        y += lineH;
        drawLabelValue('Cuotas:', `${loan.term}`, col1X, y);
        drawLabelValue('Frecuencia:', translateTxType(loan.condition_level.toString())?.toUpperCase() || 'MENSUAL', col2X, y);
        y += lineH;
        drawLabelValue('Tasa Interés:', `${loan.rate}% Mensual`, col1X, y);
        drawLabelValue('Cuota Estimada:', `$ ${formatMoney(loan.estimated_quota)}`, col2X, y);
        y += lineH;
        drawLabelValue('Inicio/Fin:', `${loan.init_date} AL ${loan.end_date}`, col1X, y);
        drawLabelValue('Aprobado Por:', loan.approved_by?.toUpperCase(), col2X, y);
        y += lineH;

        // DICCIONARIO: Método de Desembolso
        drawLabelValue('Vía Desembolso:', translateTxType(loan.disbursement_method)?.toUpperCase(), col1X, y);
        drawLabelValue('Notas Desemb.:', loan.disbursement_notes?.toUpperCase() || 'N/A', col2X, y);
        y += lineH;

        let offset = drawLabelValueLong('Notas Comité:', loan.committee_notes || 'N/A', col1X, y, contentWidth - 40);
        y += offset;

        if (loan.rejection_reason) {
          doc.setTextColor(200, 0, 0);
          offset = drawLabelValueLong('Razón Rechazo:', loan.rejection_reason, col1X, y, contentWidth - 40);
          doc.setTextColor(0, 0, 0);
          y += offset;
        }
        y += 2;
      }


      // ====================================================================
      // SECCIÓN: ANÁLISIS FINANCIERO Y DEL NEGOCIO
      // ====================================================================
      if (opts.showEvaluacion && evaluation) {
        drawSectionTitle('Evaluación del Negocio y Capacidad de Pago');
        doc.setFontSize(8);
        const col1X = margin;
        const col2X = margin + (contentWidth / 2) + 5;
        let lineH = 5.5;

        drawLabelValue('Nombre Negocio:', evaluation.business_name?.toUpperCase(), col1X, y);

        // DICCIONARIO: Tipo de Negocio
        drawLabelValue('Tipo Negocio:', getGlobalText(evaluation.business_type, 8), col2X, y);
        y += lineH;
        let offset = drawLabelValueLong('Descripción:', evaluation.business_description?.toUpperCase(), col1X, y, contentWidth - 40);
        y += offset;

        drawLabelValue('Estabilidad:', getGlobalText(evaluation.business_stability, 9), col1X, y);

        // DICCIONARIO: Clientes Atendidos (En caso de que sean IDs)
        drawLabelValue('Clientes Atendidos:', getGlobalText(evaluation.clients_served, 10), col2X, y);
        y += lineH;

        doc.setDrawColor(220, 220, 220);
        doc.line(margin, y, pageWidth - margin, y);
        y += 4;

        drawLabelValue('Ingresos Semanal:', `$ ${formatMoney(evaluation.total_weekly_income)}`, col1X, y);
        drawLabelValue('Ingresos Mensual:', `$ ${formatMoney(evaluation.total_monthly_income)}`, col2X, y);
        y += lineH;
        drawLabelValue('Costo Ventas (%):', `${evaluation.cost_of_sales_percent}%`, col1X, y);
        drawLabelValue('Costo Mensual:', `$ ${formatMoney(evaluation.estimated_monthly_cost)}`, col2X, y);
        y += lineH;
        drawLabelValue('Egresos Mensual:', `$ ${formatMoney(evaluation.monthly_expenses)}`, col1X, y);
        drawLabelValue('Deudas Mensual:', `$ ${formatMoney(evaluation.monthly_debt_payment)}`, col2X, y);
        y += lineH;

        doc.setFont('helvetica', 'bold');
        drawLabelValue('Disponible Diario:', `$ ${formatMoney(evaluation.final_daily_available)}`, col1X, y);
        // Ratio de endeudamiento (Por defecto 0 si no viene definido)
        drawLabelValue('Ratio Endeudam.:', `${evaluation.debt_ratio || 0}%`, col2X, y);
        doc.setFont('helvetica', 'normal');
        y += lineH + 2;
      }


      // ====================================================================
      // SECCIÓN: GARANTÍAS RESPALDANDO EL CRÉDITO
      // ====================================================================
      if (opts.showGarantias) {
        drawSectionTitle('Detalle de Garantías');
        if (!collaterals || collaterals.length === 0) {
          doc.setFont('helvetica', 'italic');
          doc.text('(Sin garantías registradas).', margin, y);
          y += 7.5;
        } else {
          doc.setFont('helvetica', 'bold');
          doc.text('TIPO', margin, y);
          doc.text('DESCRIPCIÓN', margin + 30, y);
          doc.text('VALOR EST.', pageWidth - margin, y, { align: 'right' });
          y += 2;
          doc.setDrawColor(200, 200, 200);
          doc.line(margin, y, pageWidth - margin, y);
          y += 4;

          doc.setFont('helvetica', 'normal');
          collaterals.forEach(g => {
            checkPageBreak(10);

            // DICCIONARIO: Tipo de Garantía
            const tipoGarantia = translateTxType(g.collateral_type)?.toUpperCase() || 'N/A';
            doc.text(tipoGarantia, margin, y);
            doc.text(g.description?.substring(0, 60) || 'N/A', margin + 30, y);
            doc.text(`$ ${formatMoney(g.guarantee_amount)}`, pageWidth - margin, y, { align: 'right' });
            y += 5;
          });
          y += 3;
        }
      }



      // --- SISTEMA DE COORDENADAS UNIFICADO PARA TABLAS FINANCIERAS ---
      const stdColNo = margin;
      const stdColFecha = margin + 20;
      const stdColTipo = margin + 40; // Usado para "TIPO" en transacciones
      // Anclas alineadas a la derecha
      const stdColCuotaMonto = margin + 85;
      const stdColCapPagado = margin + 105;
      const stdColInteres = margin + 125;
      const stdColMora = margin + 145;
      const stdColCargos = margin + 165;
      const stdColSaldoEstado = pageWidth - margin;

      // ====================================================================
      // SECCIÓN: PLAN DE PAGOS TEÓRICO (PROYECCIÓN)
      // ====================================================================
      if (opts.showPlanTeorico) {
        drawSectionTitle('Plan de Pagos Teórico');

        if (!theoretical_plans || theoretical_plans.length === 0) {
          doc.setFont('helvetica', 'italic');
          doc.text('No hay un plan teórico registrado.', margin, y);
          y += 7.5;
        } else {
          const drawPlanHeaders = () => {
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(7.5);
            doc.text('N°', stdColNo, y);
            doc.text('FECHA PAGO', stdColFecha, y);
            doc.text('CUOTA', stdColCuotaMonto, y, { align: 'right' });
            doc.text('CAPITAL', stdColCapPagado, y, { align: 'right' });
            doc.text('INTERÉS', stdColInteres, y, { align: 'right' });
            doc.text('I. MORA', stdColMora, y, { align: 'right' });
            doc.text('CARGOS', stdColCargos, y, { align: 'right' });
            doc.text('SALDO.', stdColSaldoEstado, y, { align: 'right' });
            y += 2;
            doc.setDrawColor(0, 0, 0);
            doc.line(margin, y, pageWidth - margin, y);
            y += 4;
          };

          drawPlanHeaders();
          doc.setFont('helvetica', 'normal');

          let totCuota = 0, totCap = 0, totInt = 0, totMora = 0, totCargos = 0;

          theoretical_plans.forEach((plan, idx) => {
            if (checkPageBreak(10)) drawPlanHeaders();

            const shortDate = plan.due_date ? plan.due_date.split(' ')[0] : '-';

            const valCuota = Number(plan.total_amount || 0);
            const valCap = Number(plan.capital || plan.principal || 0);
            const valInt = Number(plan.interest || 0);
            const valMora = Number(plan.late_interest || plan.penalty || 0);
            const valCargos = Number(plan.charges || 0);

            // Sumatorias
            totCuota += valCuota;
            totCap += valCap;
            totInt += valInt;
            totMora += valMora;
            totCargos += valCargos;

            doc.text(String(plan.installment_number || idx + 1), stdColNo, y);
            doc.text(shortDate, stdColFecha, y);
            doc.text(formatMoney(valCuota), stdColCuotaMonto, y, { align: 'right' });
            doc.text(formatMoney(valCap), stdColCapPagado, y, { align: 'right' });
            doc.text(formatMoney(valInt), stdColInteres, y, { align: 'right' });
            doc.text(formatMoney(valMora), stdColMora, y, { align: 'right' });
            doc.text(formatMoney(valCargos), stdColCargos, y, { align: 'right' });
            doc.text(formatMoney(plan.balance || plan.remaining_balance), stdColSaldoEstado, y, { align: 'right' });

            doc.setDrawColor(230, 230, 230);
            doc.setLineWidth(0.1);
            doc.line(margin, y + 2, pageWidth - margin, y + 2);
            y += 5.5;
          });

          // FILA DE TOTALES
          doc.setFont('helvetica', 'bold');
          doc.setDrawColor(0, 0, 0);
          doc.setLineWidth(0.3);
          doc.line(margin, y, pageWidth - margin, y);
          y += 5;
          doc.text('TOTALES:', stdColFecha + 25, y, { align: 'right' });
          doc.text(formatMoney(totCuota), stdColCuotaMonto, y, { align: 'right' });
          doc.text(formatMoney(totCap), stdColCapPagado, y, { align: 'right' });
          doc.text(formatMoney(totInt), stdColInteres, y, { align: 'right' });
          doc.text(formatMoney(totMora), stdColMora, y, { align: 'right' });
          doc.text(formatMoney(totCargos), stdColCargos, y, { align: 'right' });
          doc.line(margin, y + 2, pageWidth - margin, y + 2);
          doc.setLineWidth(0.1);
          doc.line(margin, y + 3, pageWidth - margin, y + 3);
          y += 6;
        }
      }

      // ====================================================================
      // SECCIÓN: PLAN DE PAGOS ACTIVO (REALIDAD)
      // ====================================================================
      if (opts.showPlanActivo) {
        drawSectionTitle('Plan de Pagos Activo');

        if (!payment_plans || payment_plans.length === 0) {
          doc.setFont('helvetica', 'italic');
          doc.text('No hay un plan activo registrado.', margin, y);
          y += 7.5;
        } else {
          const drawActivePlanHeaders = () => {
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(7.5);
            doc.text('N°', stdColNo, y);
            doc.text('VENCIMIENTO', stdColFecha, y);
            doc.text('CUOTA', stdColCuotaMonto, y, { align: 'right' });
            doc.text('CAPITAL', stdColCapPagado, y, { align: 'right' });

            // Reutilizamos las posiciones estándar empujándolas para que quepa todo
            doc.text('INTERÉS', stdColInteres, y, { align: 'right' });
            doc.text('CARGOS', stdColMora, y, { align: 'right' });
            doc.text('SALDO', stdColCargos, y, { align: 'right' });
            doc.text('ESTADO', stdColSaldoEstado, y, { align: 'right' });

            y += 2;
            doc.setDrawColor(0, 0, 0);
            doc.line(margin, y, pageWidth - margin, y);
            y += 4;
          };

          drawActivePlanHeaders();
          doc.setFont('helvetica', 'normal');

          let totCuota = 0, totPagado = 0, totMora = 0, totCargos = 0, totSaldo = 0;

          payment_plans.forEach((plan) => {
            if (checkPageBreak(10)) drawActivePlanHeaders();

            const shortDate = plan.due_date ? plan.due_date.split(' ')[0] : '-';

            const valCuota = Number(plan.total_amount || 0);
            const valPagado = Number(plan.capital || 0);
            const valMora = Number(plan.interest || 0);
            const valCargos = Number(plan.charges || 0);
            // Saldo real de la cuota: Lo que era + Mora + Cargos - Lo que ya pagó
            const saldoCuota = plan.balance || plan.remaining_balance;

            // Sumatorias globales
            totCuota += valCuota;
            totPagado += valPagado;
            totMora += valMora;
            totCargos += valCargos;
            totSaldo += saldoCuota;

            doc.text(String(plan.installment_number), stdColNo, y);
            doc.text(shortDate, stdColFecha, y);
            doc.text(formatMoney(valCuota), stdColCuotaMonto, y, { align: 'right' });
            doc.text(formatMoney(valPagado), stdColCapPagado, y, { align: 'right' });
            doc.text(formatMoney(valMora), stdColInteres, y, { align: 'right' });
            doc.text(formatMoney(valCargos), stdColMora, y, { align: 'right' });
            doc.text(formatMoney(saldoCuota), stdColCargos, y, { align: 'right' });

            if (plan.status === 'PAID') doc.setTextColor(0, 150, 0);
            else if (plan.status === 'PENDING') doc.setTextColor(200, 100, 0);

            doc.setFont('helvetica', 'bold');
            doc.text(translateTxType(plan.status)?.toUpperCase() || plan.status, stdColSaldoEstado, y, { align: 'right' });

            doc.setTextColor(0, 0, 0);
            doc.setFont('helvetica', 'normal');

            doc.setDrawColor(230, 230, 230);
            doc.setLineWidth(0.1);
            doc.line(margin, y + 2, pageWidth - margin, y + 2);
            y += 5.5;
          });

          // FILA DE TOTALES
          doc.setFont('helvetica', 'bold');
          doc.setDrawColor(0, 0, 0);
          doc.setLineWidth(0.3);
          doc.line(margin, y, pageWidth - margin, y);
          y += 5;
          doc.text('TOTALES:', stdColFecha + 25, y, { align: 'right' });
          doc.text(formatMoney(totCuota), stdColCuotaMonto, y, { align: 'right' });
          doc.text(formatMoney(totPagado), stdColCapPagado, y, { align: 'right' });
          doc.text(formatMoney(totMora), stdColInteres, y, { align: 'right' });
          doc.text(formatMoney(totCargos), stdColMora, y, { align: 'right' });
          doc.text(formatMoney(totSaldo), stdColCargos, y, { align: 'right' });

          doc.line(margin, y + 2, pageWidth - margin, y + 2);
          doc.setLineWidth(0.1);
          doc.line(margin, y + 3, pageWidth - margin, y + 3);
          y += 6;
        }
      }

      // ====================================================================
      // SECCIÓN: HISTORIAL DE MOVIMIENTOS Y TRANSACCIONES
      // ====================================================================
      if (opts.showTransacciones) {
        drawSectionTitle('Historial de Movimientos Transaccionales');

        if (!transactions || transactions.length === 0) {
          doc.setFont('helvetica', 'italic');
          doc.text('El crédito no registra movimientos a la fecha.', margin, y);
          y += 7.5;
        } else {
          const drawTxHeaders = () => {
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(7.5);
            doc.text('FECHA', stdColNo, y);
            doc.text('N° TRANS.', stdColFecha, y);
            doc.text('TIPO', stdColTipo, y);
            doc.text('MONTO', stdColCuotaMonto, y, { align: 'right' });
            doc.text('CAPITAL', stdColCapPagado, y, { align: 'right' });
            doc.text('INTERÉS', stdColInteres, y, { align: 'right' });
            doc.text('I. MORA', stdColMora, y, { align: 'right' });
            doc.text('CARGOS', stdColCargos, y, { align: 'right' });
            doc.text('SALDO', stdColSaldoEstado, y, { align: 'right' });
            y += 2;
            doc.setDrawColor(0, 0, 0);
            doc.line(margin, y, pageWidth - margin, y);
            y += 4;
          };

          drawTxHeaders();
          doc.setFont('helvetica', 'normal');

          let totMonto = 0, totCapital = 0, totInteres = 0, totMora = 0, totCargos = 0;

          transactions.forEach((tx) => {
            if (checkPageBreak(10)) drawTxHeaders();

            const shortDate = tx.transaction_date ? tx.transaction_date.split(' ')[0] : '-';
            const shortId = tx.id ? tx.id.toString().substring(0, 8) : '-';
            const tipoTraducido = translateTxType(tx.transaction_type);

            const valMonto = Number(tx.total_amount || 0);
            const valCap = Number(tx.principal_applied || 0);
            const valInt = Number(tx.interest_applied || 0);
            const valMora = Number(tx.late_fee_applied || 0);
            const valCargos = Number(tx.charges_amount || 0);
            if(tx.transaction_type=='PAYMENT'){
              // Sumatorias
              totMonto += valMonto;
              totCapital += valCap;
              totInteres += valInt;
              totMora += valMora;
              totCargos += valCargos;
            }


            doc.text(shortDate, stdColNo, y);
            doc.text(shortId, stdColFecha, y);
            doc.text(tipoTraducido || '-', stdColTipo, y);

            doc.text(formatMoney(valMonto), stdColCuotaMonto, y, { align: 'right' });
            doc.text(formatMoney(valCap), stdColCapPagado, y, { align: 'right' });
            doc.text(formatMoney(valInt), stdColInteres, y, { align: 'right' });
            doc.text(formatMoney(valMora), stdColMora, y, { align: 'right' });
            doc.text(formatMoney(valCargos), stdColCargos, y, { align: 'right' });

            doc.setFont('helvetica', 'bold');
            doc.text(formatMoney(tx.balance_after), stdColSaldoEstado, y, { align: 'right' });
            doc.setFont('helvetica', 'normal');

            doc.setDrawColor(230, 230, 230);
            doc.setLineWidth(0.1);
            doc.line(margin, y + 2, pageWidth - margin, y + 2);
            y += 5.5;
          });

          // FILA DE TOTALES
          doc.setFont('helvetica', 'bold');
          doc.setDrawColor(0, 0, 0);
          doc.setLineWidth(0.3);
          doc.line(margin, y, pageWidth - margin, y);
          y += 5;
          doc.text('TOTALES:', stdColTipo + 20, y, { align: 'right' });
          doc.text(formatMoney(totMonto), stdColCuotaMonto, y, { align: 'right' });
          doc.text(formatMoney(totCapital), stdColCapPagado, y, { align: 'right' });
          doc.text(formatMoney(totInteres), stdColInteres, y, { align: 'right' });
          doc.text(formatMoney(totMora), stdColMora, y, { align: 'right' });
          doc.text(formatMoney(totCargos), stdColCargos, y, { align: 'right' });
          doc.line(margin, y + 2, pageWidth - margin, y + 2);
          doc.setLineWidth(0.1);
          doc.line(margin, y + 3, pageWidth - margin, y + 3);
          y += 6;
        }
      }

      // ====================================================================
      // SECCIÓN: ANEXOS FOTOGRÁFICOS Y DOCUMENTALES
      // ====================================================================
      if (opts.showAnexos) {
        drawSectionTitle('Anexos Fotográficos y Firmas');

        const drawAnexo = async (source, title, isBase64 = false) => {
          if (!source) return;

          checkPageBreak(100);

          doc.setFont('helvetica', 'bold');
          doc.setFontSize(9);
          doc.text(title, margin, y);
          y += 5;

          try {
            const imgData = isBase64 ? source : await loadImage(source);
            if (imgData) {
              const maxW = 80;
              const maxH = 80;
              let renderW = 80;
              let renderH = 80;

              const width = isBase64 ? maxW : (imgData.naturalWidth || imgData.width || maxW);
              const height = isBase64 ? maxH : (imgData.naturalHeight || imgData.height || maxH);
              const ratio = width / height;

              renderH = maxH;
              renderW = renderH * ratio;

              if (renderW > maxW) {
                renderW = maxW;
                renderH = renderW / ratio;
              }

              const format = isBase64 ? 'PNG' : undefined;
              doc.addImage(imgData, format, margin, y, renderW, renderH, undefined, 'FAST');

              y += renderH + 10;
            } else {
              doc.setFont('helvetica', 'italic');
              doc.text('Imagen no disponible.', margin, y);
              y += 10;
            }
          } catch (err) {
            doc.setFont('helvetica', 'italic');
            doc.text('Error al cargar la imagen.', margin, y);
            y += 10;
          }
        };

        await drawAnexo(customer.photo_url, 'A. Fotografía de Perfil del Cliente');
        await drawAnexo(customer.document_front_url, 'B. Documento de Identidad (Frente)');
        await drawAnexo(customer.document_back_url, 'C. Documento de Identidad (Reverso)');

        if (evaluation) {
          await drawAnexo(evaluation.photo_url_1, 'D. Evidencia del Negocio - Foto 1');
          await drawAnexo(evaluation.photo_url_2, 'E. Evidencia del Negocio - Foto 2');
        }

        await drawAnexo(customer.signature_base64, 'F. Firma Digital del Cliente', true);
      }


      // ====================================================================
      // FOOTER Y MARCA DE AGUA FINAL
      // ====================================================================
      const totalPages = doc.internal.getNumberOfPages();
        drawFooter(doc, { companyName: config.trade_name });

      for (let i = 1; i <= totalPages; i++) {
        doc.setPage(i);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(0, 0, 0);

        if (loan.status === 'REJECTED' || loan.status === 'RECHAZADO') {
          doc.saveGraphicsState();
          doc.setFontSize(90);
          doc.setFont('helvetica', 'bold');
          doc.setTextColor(200, 0, 0);
          doc.setGState(new doc.GState({ opacity: 0.15 }));
          doc.text('RECHAZADO', pageWidth / 2, pageHeight / 2, { align: 'center', angle: 45 });
          doc.restoreGraphicsState();
          doc.setFont('helvetica', 'normal');
          doc.setTextColor(0, 0, 0);
        }
      }

      doc.save(`Expediente_Integral_${loan.code}.pdf`);

    } catch (error) {
      console.error('Error generando expediente integral:', error);
    }
  };

  return { generateSummary };
}